import { Text, useToast } from '@chakra-ui/react';
import { sanitize } from 'dompurify';
import { groupBy } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { ShareResults } from '@lon/shared/requests';
import { PreparedDistrictAssignmentData } from '@lon/shared/types';
import { Student } from './types';

export const useShareResultHandler = ({
  useMutation,
  studentsData,
  isMultiple = false,
  onCompleted = () => {},
  withNotes = false,
}: {
  useMutation: any;
  isMultiple?: boolean;
  withNotes?: boolean;
  studentsData: (Student | PreparedDistrictAssignmentData)[];
  onCompleted?: () => void;
}): [any, any] => {
  const [shareResult, opts] = useMutation();
  const { t } = useTranslation();
  const toast = useToast();

  return [
    (onCloseModal: () => void) =>
      async (values: { notes: string; includeCorrectAnswers: boolean }) => {
        const studentIdsByAssignmentId = Object.entries(
          groupBy(studentsData, 'assignmentId')
        ).map(([assignmentId, assignments]) => [
          assignmentId,
          assignments.map((assignment) => assignment.studentId),
        ]);

        try {
          const data = await Promise.allSettled(
            studentIdsByAssignmentId.map(([assignmentId, studentIds]) =>
              shareResult({
                variables: {
                  params: {
                    assignmentId: assignmentId as string,
                    studentIds: studentIds as string[],
                    shareResults: values.includeCorrectAnswers
                      ? ShareResults.WithCorrectAnswers
                      : ShareResults.WithoutCorrectAnswers,
                    notes: withNotes ? values.notes : undefined,
                  },
                },
              })
            )
          );

          if (data.every((value) => value.status === 'fulfilled')) {
            toast({
              title: t('assignments.shareSuccessToastTitle'),
              description: (
                <Text whiteSpace="pre-wrap">
                  {isMultiple
                    ? t('assignments.shareSuccessToastDescriptionMultiple', {
                        amount: studentsData.length,
                      })
                    : sanitize(
                        t('assignments.shareSuccessToastDescription', {
                          name: `${studentsData[0]?.firstName} ${studentsData[0]?.lastName}`,
                          interpolation: {
                            escapeValue: false,
                          },
                        })
                      )}
                </Text>
              ),
              variant: 'success-light',
              status: 'success',
              isClosable: true,
              duration: 3000,
            });
            onCompleted();
            onCloseModal();
          } else {
            toast({
              title: t('systemMessages.038'),
              status: 'error',
              variant: 'error-light',
              isClosable: true,
              duration: null,
            });
          }
        } catch (error) {
          toast({
            title: t('systemMessages.038'),
            status: 'error',
            variant: 'error-light',
            isClosable: true,
            duration: null,
          });
        }
      },
    opts,
  ];
};
